import { unwrap, type Schemas } from '@mrigl/api-client';
import type { MessageKey } from '@mrigl/i18n';
import { LOCALES, pickLocalized, type Locale } from '@mrigl/shared';
import { Alert, Button, Card, SelectField, TextField } from '@mrigl/ui';
import { useCallback, useEffect, useState, type SubmitEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../api.js';
import { Loading } from '../components.js';
import { useI18n } from '../i18n.js';
import { useMe, useSession } from '../session.js';

const ROLE_KEYS: Readonly<Record<string, MessageKey>> = {
  owner: 'admin.role.owner',
  admin: 'admin.role.admin',
  staff: 'admin.role.staff',
  support: 'admin.role.support',
};
const STATUS_KEYS: Readonly<Record<string, MessageKey>> = {
  active: 'admin.storeStatus.active',
  suspended: 'admin.storeStatus.suspended',
  provisioning: 'admin.storeStatus.provisioning',
};

export function HomePage() {
  const { t, locale } = useI18n();
  const me = useMe();
  const name = me.user.displayName;
  return (
    <div className="mrigl-stack">
      <div className="admin-pagehead">
        <h1 className="mrigl-heading">
          {name === null ? t('admin.home.titleAnonymous') : t('admin.home.title', { name })}
        </h1>
        <Link className="mrigl-button mrigl-button--primary admin-cta" to="/stores/new">
          {t('admin.home.createStore')}
        </Link>
      </div>
      <section className="mrigl-stack" aria-labelledby="stores-title">
        <h2 id="stores-title" className="mrigl-subheading">
          {t('admin.home.stores')}
        </h2>
        {me.stores.length === 0 ? (
          <Card>
            <p className="mrigl-muted">{t('admin.home.noStores')}</p>
          </Card>
        ) : (
          <ul className="admin-storelist">
            {me.stores.map((store) => {
              const roleKey = ROLE_KEYS[store.role];
              const statusKey = STATUS_KEYS[store.status];
              const storeName = pickLocalized(store.name, locale, 'fr') ?? store.slug;
              return (
                <li key={store.storeId} className="admin-storecard">
                  <div className="admin-storecard__head">
                    <span className="admin-storechip__avatar" aria-hidden="true">
                      {storeName.slice(0, 1).toUpperCase()}
                    </span>
                    <strong>{storeName}</strong>
                  </div>
                  <div className="admin-storecard__meta">
                    <span className="admin-status">
                      {roleKey === undefined ? store.role : t(roleKey)}
                    </span>
                    <span
                      className={`admin-status ${store.status === 'active' ? 'admin-status--delivered' : ''}`}
                    >
                      {statusKey === undefined ? store.status : t(statusKey)}
                    </span>
                  </div>
                  <div className="admin-storecard__links">
                    {store.status === 'provisioning' &&
                    (store.role === 'owner' || store.role === 'admin') ? (
                      <Link
                        className="mrigl-button mrigl-button--primary"
                        to={`/stores/${store.storeId}/setup`}
                      >
                        {t('admin.setup.finish')}
                      </Link>
                    ) : null}
                    <Link
                      className={`mrigl-button ${store.status === 'provisioning' ? 'mrigl-button--secondary' : 'mrigl-button--primary'}`}
                      to={`/stores/${store.storeId}`}
                    >
                      {t('admin.home.openStore')}
                    </Link>
                    {store.role === 'owner' || store.role === 'admin' ? (
                      <Link
                        className="mrigl-button mrigl-button--secondary"
                        to={`/stores/${store.storeId}/audit`}
                      >
                        {t('admin.home.auditLog')}
                      </Link>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

export function AccountPage() {
  const { t, setLocale } = useI18n();
  const me = useMe();
  const { refresh } = useSession();
  const [displayName, setDisplayName] = useState(me.user.displayName ?? '');
  const [preferredLocale, setPreferredLocale] = useState<Locale>(me.user.preferredLocale);
  // Messages are rendered with the current t(), so a confirmation follows a language switch.
  const [status, setStatus] = useState<
    { tone: 'success' } | { tone: 'error'; error: unknown } | null
  >(null);
  const [busy, setBusy] = useState(false);

  async function save(event: SubmitEvent) {
    event.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      await unwrap(
        api.PATCH('/admin-api/v1/me', {
          body: {
            preferredLocale,
            ...(displayName.trim().length > 0 ? { displayName: displayName.trim() } : {}),
          },
        }),
      );
      setLocale(preferredLocale);
      await refresh();
      setStatus({ tone: 'success' });
    } catch (failure) {
      setStatus({ tone: 'error', error: failure });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mrigl-stack">
      <h1 className="mrigl-heading">{t('admin.account.title')}</h1>
      {status === null ? null : (
        <Alert tone={status.tone}>
          {status.tone === 'success' ? t('admin.saved') : errorMessage(t, status.error)}
        </Alert>
      )}
      <form className="mrigl-card mrigl-stack" onSubmit={(event) => void save(event)}>
        <TextField label={t('admin.account.email')} value={me.user.email ?? ''} readOnly ltr />
        <TextField
          label={t('admin.account.displayName')}
          name="displayName"
          autoComplete="name"
          maxLength={120}
          value={displayName}
          onChange={(event) => {
            setDisplayName(event.target.value);
          }}
        />
        <SelectField
          label={t('admin.account.preferredLocale')}
          value={preferredLocale}
          onChange={(event) => {
            setPreferredLocale(event.target.value as Locale);
          }}
          options={LOCALES.map((value) => ({
            value,
            label: t(`admin.language.${value}`),
            lang: value,
          }))}
        />
        <Button type="submit" busy={busy}>
          {t('admin.save')}
        </Button>
      </form>
    </div>
  );
}

type SessionItem = Schemas['SessionList']['items'][number];
type SecurityEvent = Schemas['SecurityEvents']['items'][number];

export function SecurityPage() {
  const { t, formatDateTime } = useI18n();
  const me = useMe();
  const { refresh } = useSession();
  const [sessions, setSessions] = useState<SessionItem[] | null>(null);
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [newCodes, setNewCodes] = useState<string[] | null>(null);
  const [disableCode, setDisableCode] = useState('');
  const [status, setStatus] = useState<{ tone: 'error' | 'success'; message: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const [sessionList, activity] = await Promise.all([
        unwrap(api.GET('/admin-api/v1/me/sessions')),
        unwrap(api.GET('/admin-api/v1/me/security-events')),
      ]);
      setSessions(sessionList.items);
      setEvents(activity.items);
    } catch (failure) {
      setStatus({ tone: 'error', message: errorMessage(t, failure) });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<string | null>) {
    setStatus(null);
    try {
      const message = await action();
      if (message !== null) setStatus({ tone: 'success', message });
      await load();
    } catch (failure) {
      setStatus({ tone: 'error', message: errorMessage(t, failure) });
    }
  }

  return (
    <div className="mrigl-stack">
      <h1 className="mrigl-heading">{t('admin.security.title')}</h1>
      {status === null ? null : <Alert tone={status.tone}>{status.message}</Alert>}

      <Card labelledBy="mfa-title">
        <h2 id="mfa-title" className="mrigl-subheading">
          {t('admin.security.mfa.title')}
        </h2>
        {me.mfa.totpEnrolled ? (
          <>
            <p>{t('admin.security.mfa.on')}</p>
            <form
              className="mrigl-stack"
              onSubmit={(event) => {
                event.preventDefault();
                void run(async () => {
                  const code = disableCode.trim();
                  await unwrap(
                    api.POST('/admin-api/v1/me/mfa/totp/disable', {
                      body: code === '' ? {} : { code },
                    }),
                  );
                  setDisableCode('');
                  setNewCodes(null);
                  await refresh();
                  return t('admin.security.mfa.disabled');
                });
              }}
            >
              <TextField
                label={t('admin.mfa.code')}
                hint={t('admin.security.mfa.codeHint')}
                name="code"
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                ltr
                className="mrigl-code"
                value={disableCode}
                onChange={(event) => {
                  setDisableCode(event.target.value.replace(/\D/g, ''));
                }}
              />
              <Button type="submit" variant="secondary">
                {t('admin.security.mfa.disable')}
              </Button>
            </form>
          </>
        ) : (
          <>
            <p>{t('admin.security.mfa.off')}</p>
            <Link className="mrigl-button mrigl-button--primary admin-cta" to="/security/mfa">
              {t('admin.security.mfa.enable')}
            </Link>
          </>
        )}
      </Card>

      <Card labelledBy="sessions-title">
        <h2 id="sessions-title" className="mrigl-subheading">
          {t('admin.security.sessions')}
        </h2>
        {sessions === null ? (
          <Loading />
        ) : (
          <ul className="mrigl-list">
            {sessions.map((session) => (
              <li key={session.id} className="mrigl-row">
                <span>
                  {session.current ? <strong>{t('admin.security.thisDevice')}</strong> : null}{' '}
                  <span className="mrigl-ltr">
                    {session.userAgent ?? t('admin.security.unknownDevice')}
                  </span>
                </span>
                <span className="mrigl-muted">
                  {t('admin.security.lastActive', { date: formatDateTime(session.lastSeenAt) })}
                </span>
                {session.current ? null : (
                  <Button
                    variant="secondary"
                    onClick={() =>
                      void run(async () => {
                        await unwrap(
                          api.DELETE('/admin-api/v1/me/sessions/{sessionId}', {
                            params: { path: { sessionId: session.id } },
                          }),
                        );
                        return null;
                      })
                    }
                  >
                    {t('admin.security.revoke')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        <Button
          variant="secondary"
          onClick={() =>
            void run(async () => {
              const { revoked } = await unwrap(api.POST('/admin-api/v1/me/sessions/revoke-others'));
              return t('admin.security.revokedOthers', { count: revoked });
            })
          }
        >
          {t('admin.security.revokeOthers')}
        </Button>
      </Card>

      {me.mfa.totpEnrolled ? (
        <Card labelledBy="recovery-title">
          <h2 id="recovery-title" className="mrigl-subheading">
            {t('admin.security.recoveryCodes')}
          </h2>
          <p className="mrigl-muted">
            {t('admin.security.recoveryRemaining', { count: me.mfa.recoveryCodesRemaining })}
          </p>
          {newCodes === null ? null : (
            <ul
              className="mrigl-list mrigl-ltr mrigl-code"
              aria-label={t('admin.mfa.recoveryTitle')}
            >
              {newCodes.map((value) => (
                <li key={value}>{value}</li>
              ))}
            </ul>
          )}
          <Button
            variant="secondary"
            onClick={() =>
              void run(async () => {
                const { recoveryCodes } = await unwrap(
                  api.POST('/admin-api/v1/me/mfa/recovery-codes'),
                );
                setNewCodes(recoveryCodes);
                await refresh();
                return t('admin.mfa.recoveryIntro');
              })
            }
          >
            {t('admin.security.regenerate')}
          </Button>
        </Card>
      ) : null}

      <Card labelledBy="activity-title">
        <h2 id="activity-title" className="mrigl-subheading">
          {t('admin.security.activity')}
        </h2>
        <ul className="mrigl-list">
          {events.map((event) => (
            <li key={event.id} className="mrigl-row">
              <span>{t(`admin.security.event.${event.event}` as MessageKey)}</span>
              <span className="mrigl-muted">{formatDateTime(event.occurredAt)}</span>
              {event.ipAddress === null ? null : (
                <span className="mrigl-muted mrigl-ltr">{event.ipAddress}</span>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
